/**
 * PhD Service
 *
 * Handles all Flux PhD API calls across two backends:
 * - PhD Simulation Service (REACT_APP_PHD_API_URL): topologies, simulation, comparison
 * - Main Backend API (REACT_APP_API_URL): profile CRUD via /api/site_dna/v1/flux-phd/profiles
 *
 * Convention: currentUser is always the last parameter (matches evProjectService.js)
 */

import { apiGet, apiPost, apiDelete, handleApiResponse, getAuthHeaders } from './api';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';
const PHD_PROFILES_API = '/api/site_dna/v1/flux-phd/profiles';

// ============================================================
// Helper for PhD simulation service requests (different base URL)
// ============================================================

async function phdFetch(endpoint, options = {}) {
    const response = await fetch(`${PHD_API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });
    return response;
}

async function phdFetchAuth(endpoint, options = {}, currentUser) {
    const headers = await getAuthHeaders(currentUser);
    const response = await fetch(`${PHD_API_BASE}${endpoint}`, {
        ...options,
        headers: {
            ...headers,
            ...options.headers,
        },
    });
    return response;
}

// ============================================================
// Topologies (PhD service — no auth required)
// ============================================================

export async function getTopologies() {
    const response = await phdFetch('/api/topologies');
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.topologies || data;
}

// ============================================================
// Simulation (PhD service — no auth for sim, auth for history)
// ============================================================

export async function runSimulation(topologyId, config) {
    const response = await phdFetch('/api/simulation', {
        method: 'POST',
        body: JSON.stringify({
            topology_id: topologyId,
            ...config,
        }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.message || data.detail || `HTTP ${response.status}: ${response.statusText}`);
    return data;
}

export async function runComparison(topologyIds, config) {
    const response = await phdFetch('/api/simulation/compare', {
        method: 'POST',
        body: JSON.stringify({
            topology_ids: topologyIds,
            ...config,
        }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.message || data.detail || `HTTP ${response.status}: ${response.statusText}`);
    return data;
}

// ============================================================
// History (PhD service — auth required)
// ============================================================

export async function getSimulationHistory(limit = 10, currentUser) {
    const response = await phdFetchAuth(`/api/simulations?limit=${limit}`, {}, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.simulations || [];
}

export async function getSimulation(simulationId, currentUser) {
    const response = await phdFetchAuth(`/api/simulations/${simulationId}`, {}, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

export async function saveSimulation(config, results, currentUser) {
    const response = await phdFetchAuth('/api/simulations', {
        method: 'POST',
        body: JSON.stringify({ config, results }),
    }, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

export async function deleteSimulation(simulationId, currentUser) {
    const response = await phdFetchAuth(`/api/simulations/${simulationId}`, {
        method: 'DELETE',
    }, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
}

// ============================================================
// Profiles (Main backend — auth required)
// ============================================================

export async function getProfiles(currentUser) {
    const response = await apiGet(PHD_PROFILES_API, currentUser);
    return handleApiResponse(response);
}

export async function getProfile(profileId, currentUser) {
    const response = await apiGet(`${PHD_PROFILES_API}/${profileId}`, currentUser);
    return handleApiResponse(response);
}

export async function saveProfile(data, currentUser) {
    const response = await apiPost(PHD_PROFILES_API, data, currentUser);
    return handleApiResponse(response);
}

export async function deleteProfile(profileId, currentUser) {
    const response = await apiDelete(`${PHD_PROFILES_API}/${profileId}`, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const errData = await response.json();
            errorDetail = errData.error || errData.message || errData.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
}
